import {
  SponsoredBrandsAction,
  SponsoredBrandsActionType,
  SponsoredBrandsState,
  SponsoredBrandsTargetForTableState,
  SponsoredBrandsTargetState,
} from "../types/sponsoredBrands"
import { ISbTargetResult } from "../../types/sbTargetApi.types"

const initialState: SponsoredBrandsState = {
  campaignData: {
    campaignsResult: [],
    currentPageNo: 0,
    defaultBid: 0,
    defaultOrder: 0,
    defaultTargetAcos: [],
    getAOVData: null,
    lastDateWithoutData: "",
    limitValue: 0,
    limitedCPCValue: null,
    maxTargetCPC: 0,
    minTargetCPC: 0,
    totalPages: 0,
    totalRecords: 0,
  },
  loading: false,
  error: null,
}

export const sponsoredBrandsReducer = (
  state = initialState,
  action: SponsoredBrandsAction
): SponsoredBrandsState => {
  switch (action.type) {
    case SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_CAMPAIGNS:
      return {
        ...state,
        loading: true,
        error: null,
      }
    case SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_CAMPAIGNS_SUCCESS:
      return {
        campaignData: action.payload,
        loading: false,
        error: null,
      }
    case SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_CAMPAIGNS_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
      }
    default:
      return state
  }
}

const initialTargetState: SponsoredBrandsTargetState = {
  targetData: [],
  loading: false,
  error: null,
  pendingCampaignIds: [],
  failedCampaignIds: [],
  requestGeneration: 0,
}

function withoutIds(ids: string[], excluded: string[]): string[] {
  const excludedKeys = new Set(excluded.map(String))
  return ids.filter((id) => !excludedKeys.has(String(id)))
}

function unionIds(ids: string[], added: string[]): string[] {
  return [...withoutIds(ids, added), ...added]
}

function upsertRequestedTargets(
  current: ISbTargetResult[],
  campaignIds: string[],
  targets: ISbTargetResult[]
): ISbTargetResult[] {
  const targetsByCampaignId = new Map(targets.map((target) => [String(target.campaignId), target]))
  const requested = campaignIds.map(
    (campaignId) => targetsByCampaignId.get(String(campaignId)) ?? { campaignId, targetData: [] }
  )
  const requestedKeys = new Set(requested.map((target) => String(target.campaignId)))
  return [
    ...current.filter((target) => !requestedKeys.has(String(target.campaignId))),
    ...requested,
  ]
}

export const sponsoredBrandsTargetReducer = (
  state = initialTargetState,
  action: SponsoredBrandsAction
): SponsoredBrandsTargetState => {
  switch (action.type) {
    case SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_TARGETS:
      return {
        ...state,
        loading: true,
        error: null,
        pendingCampaignIds: unionIds(state.pendingCampaignIds, action.payload.campaignIds),
        failedCampaignIds: withoutIds(state.failedCampaignIds, action.payload.campaignIds),
      }
    case SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_TARGETS_SUCCESS: {
      if (action.payload.generation !== state.requestGeneration) {
        return state
      }
      const pendingCampaignIds = withoutIds(state.pendingCampaignIds, action.payload.campaignIds)
      return {
        ...state,
        targetData: upsertRequestedTargets(
          state.targetData,
          action.payload.campaignIds,
          action.payload.targets
        ),
        loading: pendingCampaignIds.length > 0,
        error: null,
        pendingCampaignIds,
      }
    }
    case SponsoredBrandsActionType.RESET_SPONSORED_BRANDS_TARGETS:
      return { ...initialTargetState, requestGeneration: state.requestGeneration + 1 }

    case SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_TARGETS_ERROR: {
      if (action.payload.generation !== state.requestGeneration) {
        return state
      }
      const pendingCampaignIds = withoutIds(state.pendingCampaignIds, action.payload.campaignIds)
      return {
        ...state,
        loading: pendingCampaignIds.length > 0,
        error: action.payload.error,
        pendingCampaignIds,
        failedCampaignIds: unionIds(state.failedCampaignIds, action.payload.campaignIds),
      }
    }
    default:
      return state
  }
}

const initialTargetForTableState: SponsoredBrandsTargetForTableState = {
  targetDataForTable: [],
}

export const sponsoredBrandsTargetForTableReducer = (
  state = initialTargetForTableState,
  action: SponsoredBrandsAction
): SponsoredBrandsTargetForTableState => {
  switch (action.type) {
    case SponsoredBrandsActionType.ADD_SPONSORED_BRANDS_TARGETS: {
      const incomingCampaignIds = new Set(action.payload.map((target) => target.campaignId))
      return {
        targetDataForTable: [
          ...state.targetDataForTable.filter(
            (target) => !incomingCampaignIds.has(target.campaignId)
          ),
          ...action.payload,
        ],
      }
    }
    case SponsoredBrandsActionType.UPDATE_SPONSORED_BRANDS_TARGETS_LOCAL:
      return {
        targetDataForTable: state.targetDataForTable.map((target) => {
          const findedTargetData = action.payload.find(
            (updatedTarget) => updatedTarget.campaignId === target.campaignId
          )
          return findedTargetData || target
        }),
      }
    case SponsoredBrandsActionType.RESET_SPONSORED_BRANDS_TARGETS:
      return initialTargetForTableState

    default:
      return state
  }
}
